'use client';

import * as React from "react";
import {
  checkSession,
  getCurrentUser,
  logout as apiLogout,
  nativeHandoffBlob,
  redirectToLogin,
  UNAUTHORIZED_EVENT,
  UserInfo,
} from "@/shared/lib/auth";
import { usePathname, useRouter } from "next/navigation";
import { AuthLoadingScreen } from "@/shared/components/auth/AuthLoadingScreen";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  needsOnboarding: boolean;
  userInfo: UserInfo | null;
  refreshUser: () => Promise<void>;
  login: (opts?: { idpHint?: string; returnTo?: string }) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  isLoggingOut: false,
  needsOnboarding: false,
  userInfo: null,
  refreshUser: async () => {},
  login: async () => {},
  logout: async () => {},
});

const ONBOARDING_PATH = "/onboarding";
const LOGIN_PATH = "/login";
const SIGNUP_PATH = "/signup";
const SESSION_POLL_INTERVAL_MS = 60_000;

/** Paths where we never need to redirect-to-login (avoids loops). */
function isPublicAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return (
    pathname === LOGIN_PATH ||
    pathname === SIGNUP_PATH ||
    pathname.startsWith(`${LOGIN_PATH}/`) ||
    pathname.startsWith(`${SIGNUP_PATH}/`)
  );
}

export function BFFAuthProvider({ children }: { readonly children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const fetchUser = React.useCallback(async () => {
    const session = await checkSession();
    if (!session.authenticated) {
      setIsAuthenticated(false);
      setUserInfo(null);
      return null;
    }
    const user = await getCurrentUser();
    if (!user) {
      setIsAuthenticated(false);
      setUserInfo(null);
      return null;
    }
    setIsAuthenticated(true);
    setUserInfo(user);
    return user;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        await fetchUser();
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        if (!cancelled) {
          setIsAuthenticated(false);
          setUserInfo(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchUser]);

  // Onboarding gate: bounce authenticated users with no account to /onboarding,
  // and away from it once they have one.
  React.useEffect(() => {
    if (isLoading || !isAuthenticated || !userInfo) return;
    // A desktop (WiLine Workforce) sign-in is mid-handoff on /login. Bouncing it
    // to /onboarding would drop the handoff blob and strand the app; the desktop
    // has its own onboarding screen, so let it through.
    if (nativeHandoffBlob()) return;
    const onOnboarding = pathname === ONBOARDING_PATH;
    if (userInfo.needs_onboarding && !onOnboarding) {
      router.replace(ONBOARDING_PATH);
    } else if (!userInfo.needs_onboarding && onOnboarding) {
      router.replace("/");
    }
  }, [isLoading, isAuthenticated, userInfo, pathname, router]);

  // Session-loss handler. Called when:
  //   (a) The periodic /api/auth/session probe flips authenticated -> false
  //   (b) Any session-required helper hits a 401 and dispatches the
  //       UNAUTHORIZED_EVENT on `window`.
  // Idempotent and safe to call on every tick.
  const handleSessionLost = React.useCallback(() => {
    // Already on /login or /signup — nothing to do, just reflect state.
    setIsAuthenticated(false);
    setUserInfo(null);
    if (isPublicAuthPath(pathname)) return;
    const returnTo =
      pathname && pathname !== "/" ? pathname : undefined;
    const params = new URLSearchParams();
    params.set("session_expired", "1");
    if (returnTo) params.set("return_to", returnTo);
    router.replace(`${LOGIN_PATH}?${params.toString()}`);
  }, [pathname, router]);

  // Periodic re-check: if the cookie is dead (revoked, expired, or wiped by
  // the backend's 401 middleware), this catches it within one interval even
  // if no other code happened to fire UNAUTHORIZED_EVENT.
  React.useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (isPublicAuthPath(pathname)) return;
    const id = setInterval(async () => {
      const status = await checkSession();
      if (!status.authenticated) {
        handleSessionLost();
        return;
      }
      // The active workspace is a property of the session, which every tab
      // shares. Switching in one tab leaves the others rendering the previous
      // workspace's data under the new workspace's name — reload rather than
      // try to reconcile a whole page's worth of already-fetched state.
      const active = userInfo?.active_account_uuid ?? userInfo?.account?.uuid;
      if (status.account_uuid && active && status.account_uuid !== active) {
        globalThis.location.reload();
      }
    }, SESSION_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoading, isAuthenticated, pathname, handleSessionLost, userInfo]);

  // Instant reaction: any helper that hit a 401 on a session-required
  // endpoint dispatches UNAUTHORIZED_EVENT — we don't have to wait for the
  // next poll tick.
  React.useEffect(() => {
    const onUnauthorized = () => {
      if (isAuthenticated) handleSessionLost();
    };
    globalThis.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      globalThis.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [isAuthenticated, handleSessionLost]);

  const login = React.useCallback(
    async (opts?: { idpHint?: string; returnTo?: string }) => {
      await redirectToLogin(opts);
    },
    [],
  );

  const logout = React.useCallback(async () => {
    try {
      setIsLoggingOut(true);
      await apiLogout();
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsAuthenticated(false);
      setUserInfo(null);
      router.replace("/");
    } catch (error) {
      console.error("Logout failed:", error);
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsAuthenticated(false);
      setUserInfo(null);
      router.replace("/");
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 200));
      setIsLoggingOut(false);
    }
  }, [router]);

  const refreshUser = React.useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const contextValue = React.useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      isLoggingOut,
      needsOnboarding: !!userInfo?.needs_onboarding,
      userInfo,
      refreshUser,
      login,
      logout,
    }),
    [isAuthenticated, isLoading, isLoggingOut, userInfo, refreshUser, login, logout],
  );

  if (isLoggingOut) {
    return (
      <AuthContext.Provider value={contextValue}>
        <AuthLoadingScreen isLoggingOut={true} />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
